import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

// ** MUI Components
import { Box, Typography, Tooltip,AvatarGroup,Avatar} from "@mui/material";

// ** Custom Component Imports
import CustomChip from "../../../components/mui/chip";

// ** Icon Imports
import Icon from "../../../components/icon";

// ** Logic Imports
import { GetServiceRequestRaw, GetServiceRequests } from "screens/Requests/logic/GetServiceRequest";
import { GetAllJobs } from "./GetAllJobs";
import { formatUSCurrency } from "components/CurrencyFormat";

export const useIndexLogic = () => {
  //#region Hooks

  const navigate = useNavigate();

  //#endregion

  //#region Constants

  const contentHeightFixed = false;

  //#endregion End of Constants

  //#region States

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [filteredRows, setFilteredRows] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [paginationText, setPaginationText] = useState('');
  const [openNewWorkOrderDlg, setOpenNewWorkOrderDlg] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [rowSelectionModel, setRowSelectionModel] = useState([]);
  const [openTagManagementDlg, setOpenTagManagementDlg] = useState(false);
  const [schedulingReq, setSchedulingReq] = useState(false);
  const [openScheduleAst, setOpenScheduleAst] = useState(false);
  const [selectedButton, setSelectedButton] = useState(0);
  const [jobList, setJobList] = useState([]);
  const [rules, setRules] = useState(
    { 
      canEdit: true,
      canDelete: true,
      canSubmit: true,
      nextStatuses: [],
      cannotGoBack: false
  });

  const tagManagementData = { title: "Tag Management" };
  const filterButtons = [
    { name: "Work Orders"},
    { name: "All Jobs"}
  ];

  useEffect(() => {
    const fetchJobs = async () => {
      try{
        const result = await GetAllJobs(0, 0, navigate);
        if(result){
          const formattedData = result.map((job) => ({
            ...job,
            id: job.jobID
          }))
          setJobList(formattedData);
        }
      }catch(error){
        console.error('Error fetching jobs:', error)
      }
    }
    fetchJobs();
  }, [])

  const handleFilterChange = (index) => {
    setSelectedButton(index);
  }

  const extractId = (input) => {
    return input.replace(/\D/g, '');
  };

  const calculateContentWidth = (rows, field) => {
    const canvas = document.createElement("canvas");
    const context = canvas.getContext("2d");
    context.font = "15px Lexend"; // Match the font used in your grid

    let maxWidth = 80; // Minimum width
    rows.forEach((row) => {
      const textWidth = context.measureText(row[field]).width;
      maxWidth = Math.max(maxWidth, textWidth + 16); // Add some padding
    });

    return maxWidth;
  };

  // function getInitials(users) {
  //   if (!users) return "";
    
  //   // If it's an array, map over each user object and combine initials
  //   if (Array.isArray(users)) {
  //     return users
  //       .map(user => user.userName
  //         .split(" ")
  //         .map(word => word[0]?.toUpperCase())
  //         .join("")
  //       )
  //       .join(", ");
  //   }
    
  //   // If it's a single user object, return its initials
  //   return users.userName
  //     .split(" ")
  //     .map(word => word[0]?.toUpperCase())
  //     .join("");
  // }

  //#endregion end of states

  //#region Handlers and methods

  useEffect(() => {
    fetchRequests();
  }, []);

  useEffect(() => {
    const total = filteredRows.length;
    let start = paginationModel.page * paginationModel.pageSize + 1;
    let end = Math.min((paginationModel.page + 1) * paginationModel.pageSize, total);
  
    if (total === 0) {
      setPaginationText("0-0 of 0");
      return;
    }

    if (paginationModel.pageSize === -1) {
      start = 1;
      end = total;
    }
  
    setPaginationText(`${start}-${end} of ${total}`);
  }, [filteredRows, paginationModel.page, paginationModel.pageSize]);

const fetchRequests = async () => {
  try {
    setLoading(true);
    let requests = await GetServiceRequests(0, 0, 3);
    let workOrders = requests.filter(
      (x) => x.entityType.toLowerCase() === "workorder"
    );

    // Sort: requestedOn → createDate → requestID
    workOrders.sort((a, b) => {
      const dateDiff = new Date(b.requestedOn) - new Date(a.requestedOn);

      if (dateDiff !== 0) {
        return dateDiff; // primary: requestedOn
      }

      // fallback: createDate has hours/minutes
      if (a.createDate && b.createDate) {
        return new Date(b.createDate) - new Date(a.createDate);
      }

      // final fallback: requestID
      return (b.requestID || 0) - (a.requestID || 0);
    });

    setData(workOrders);
    setFilteredRows(workOrders);
  } catch (error) {
    console.error("Error fetching Requests data:", error);
  } finally {
    setLoading(false);
  }
};

  

  useEffect(() => {
    setFilteredRows(() =>
      data.filter((row) =>
        Object.values(row).some(
          (value) =>
            value &&
            value.toString().toLowerCase().includes(searchText.toLowerCase())
        )
      )
    );
  }, [searchText]);

  const customerID = 0;
  const requestID = 0;


  const handleOpenScheduleAstDlg = () => {
    setOpenScheduleAst(true);
  };

  const handleCloseScheduleAstDlg = () => {
    setOpenScheduleAst(false);
  };

  const handleOpenNewWorkOrderDlg = () => {
    setOpenNewWorkOrderDlg(true);
  };
  const handleCloseNewWorkOrderDlg = () => {
    setOpenNewWorkOrderDlg(false);
    fetchRequests();
  };

  const handleOpenTagManagementDlg = () => setOpenTagManagementDlg(true);
  const handleCloseTagManagementDlg = () => setOpenTagManagementDlg(false);

  const handleClearSelection = () => {
    setRowSelectionModel([]);
    setSelectedRows(0);
  };

  const handleGridSelection = (selectedRows) => setSelectedRows(selectedRows);

  const handleSearch = (val) => setSearchText(val);

  const handleClearSearch = () => setSearchText("");

  const getInitials = (name) => {
    if (!name || typeof name !== 'string') return '';
    
    return name
      .split(' ')
      .filter(part => part.trim().length > 0)
      .map(part => part[0].toUpperCase())
      .join('');
  };

  

  //#endregion end of handlers and methods

  //#region Columns Configuration
  const columns = [
    {
      flex: 0.1,
      field: "id",
      minWidth:120,
      headerName: "ID",
      renderCell: ({ row }) => {
        const { id } = row;

        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: "100%",
              cursor: "pointer",
              "&:hover": {
                textDecoration: "underline",
              },
            }}
            onClick={() => navigate(`/work-orders/detail/${extractId(id)}`)}
          >
            <Typography sx={{ flexGrow: 1 }}>{id}</Typography>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      field: "workOrderTitle",
      minWidth: 180,
      headerName: "Work Order Title",
      renderCell: ({ row }) => {
        const { requestTitle,id } = row;

        return (
          <Box sx={{ display: "flex", 
            alignItems: "center", 
            height: "100%" ,
            cursor: "pointer",
            "&:hover": {
              textDecoration: "underline",
          }, }} 
          onClick={() => navigate(`/work-orders/detail/${extractId(id)}`)}>
            <Typography sx={{ flexGrow: 1 }}>{requestTitle}</Typography>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      field: "customerName",
      minWidth: 180,
      headerName: "Customer Name",
      renderCell: ({ row }) => {
        const { customerName , customerID } = row;

        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%",
             cursor: "pointer",
            "&:hover": {
              textDecoration: "underline",
          }, }} 
          onClick={() => navigate(`/customers/detail/${customerID}`)}>
           
            <Typography sx={{ flexGrow: 1 }}>{customerName}</Typography>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      field: "jobsite",
      headerName: "Jobsite",
      minWidth: 150,
      renderCell: (params) => {
        const { row } = params;

        return (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              height: "100%", // Take the full height of the cell
            }}
          >
            <Typography
              noWrap
              variant="body2"
              sx={{ color: "text.primary", fontWeight: 600 }}
            >
              {row.jobsiteName}
            </Typography>
            <Typography noWrap color="text.secondary" fontSize="small">
              {row.jobsiteAddress}
            </Typography>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      field: "status",
      headerName: "Status",
      minWidth: 150,
      renderCell: ({ row }) => {
        const { status } = row;

        return (
          <CustomChip
            rounded
            size="small"
            skin="light"
            sx={{ lineHeight: 1 }}
            color={
              status === "Not Viewed" || status === "Under Review"  ? "info" : 
              status === "Completed" || status === 'Submitted'  ? "success" :
              status === "On Hold" || status === "In Progress"  ? "warning" : 
              status === "Canceled" || status === "Closed" ? "error" :  
              "secondary"
            }
            label={status}
          />
        );
      },
    },
    {
      flex: 0.1,
      field: "assignedTo",
      headerName: "Assigned To",
      minWidth: 140,
      renderCell: ({ row }) => {
        const { assignedTo } = row;
        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            {assignedTo && assignedTo.length > 0 ? (
              <AvatarGroup
                className="pull-up"
                max={4}
                sx={{
                  "& .MuiAvatar-root": { width: 25, height: 25 },
                  "& .MuiAvatarGroup-avatar": { width: 25, height: 25 },
                }}
              >
                {assignedTo.map((assignee) => (
                  <Tooltip title={assignee.userName}>
                    <Avatar
                      src="/images/avatars/4.png"
                      alt="employee-picture"
                      sx={{ width: 25, height: 25, fontSize: '12px' }}
                    >{getInitials(assignee.userName)}</Avatar>
                  </Tooltip>
                ))}
              </AvatarGroup>
            ) : (
              <Typography variant='body2' color='text.secondary'>Unassigned</Typography>
            )}
          </Box>
        );
      },
    },
   {
  flex: 0.1,
  field: "completeWorkBy",
  headerName: "Due Date",
  minWidth: 140,
  renderCell: ({ row }) => {
    const { completeWorkBy } = row;

    return (
      <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
        <Typography sx={{ flexGrow: 1 }}>
          {completeWorkBy === "1/1/1" ? "-" : completeWorkBy}
        </Typography>
      </Box>
    );
  },
},
    {
      flex: 0.1,
      field: "totalbudgetedHours",
      headerName: "Total Est Hours",
      minWidth: 170,
      renderCell: ({ row }) => {
        const { totalbudgetedHours } = row;

        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Typography sx={{ flexGrow: 1 }}>{totalbudgetedHours} hrs</Typography>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      field: "totalactualHours",
      headerName: "Total Actual Hours",
      minWidth: 190,
      renderCell: ({ row }) => {
        const { totalactualHours } = row;

        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Typography sx={{ flexGrow: 1 }}>{formatUSCurrency(totalactualHours)}</Typography>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      field: "totalbudgetedCost",
      headerName: "Total Est Cost ($)",
      minWidth: 160,
      renderCell: ({ row }) => {
        const { totalbudgetedCost } = row;

        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Typography sx={{ flexGrow: 1 }}>{formatUSCurrency(totalbudgetedCost)}</Typography>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      field: "totalactualCost",
      headerName: "Total Actual Cost ($)",
      minWidth: 190,
      renderCell: ({ row }) => {
        const { totalactualCost } = row;

        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Typography sx={{ flexGrow: 1 }}>{formatUSCurrency(totalactualCost)}</Typography>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      field: "totalestimatedProfit",
      headerName: "Total Est Profit ($)",
      minWidth: 170,
      renderCell: ({ row }) => {
        const { totalestimatedProfit } = row;

        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Typography sx={{ flexGrow: 1 }}>{formatUSCurrency(totalestimatedProfit)}</Typography>
          </Box>
        );
      },
    },
   {
  flex: 0.1,
  field: "schedulestartdate",
  headerName: "Scheduled Start Date",
  minWidth: 220,
  renderCell: ({ row }) => {
    const { schedulestartdate } = row;

    return (
      <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
        <Typography sx={{ flexGrow: 1 }}>
          {schedulestartdate === "1/1/1" ? "-" : schedulestartdate}
        </Typography>
      </Box>
    );
  },
},
{
  flex: 0.1,
  field: "scheduleenddate",
  headerName: "Scheduled End Date",
  minWidth: 200,
  renderCell: ({ row }) => {
    const { scheduleenddate } = row;

    return (
      <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
        <Typography sx={{ flexGrow: 1 }}>
          {scheduleenddate === "1/1/1" ? "-" : scheduleenddate}
        </Typography>
      </Box>
    );
  },
},
    {
      flex: 0.1,
      field: "createdBy",
      headerName: "Created By",
      minWidth: 160,
      renderCell: ({ row }) => {
        const { createBy } = row;

        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Typography sx={{ flexGrow: 1 }}>{createBy}</Typography>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      field: "createdDate",
      headerName: "Created Date",
      minWidth: 160,
      renderCell: ({ row }) => {
        const { requestedOn } = row;

        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Typography sx={{ flexGrow: 1 }}>{requestedOn}</Typography>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      field: "completionPercentage",
      headerName: "Completion Percentage",
      minWidth: 230,
      renderCell: ({ row }) => {
        const { completedjobs, totaljobs } = row;
        const completionPercentage = totaljobs > 0 ? (completedjobs / totaljobs * 100).toFixed(2) : "0.00";

        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Typography sx={{ flexGrow: 1 }}>{completionPercentage}%</Typography>
          </Box>
        );
      },
    },
  ];
  //#endregion Columns Configuration

  const IndexProps = {
    columns,
    contentHeightFixed,
    handleOpenNewWorkOrderDlg,
    handleCloseNewWorkOrderDlg,
    schedulingReq,
    setSchedulingReq,
    openScheduleAst,
    setOpenScheduleAst,
    handleOpenScheduleAstDlg,
    handleCloseScheduleAstDlg,
    handleOpenTagManagementDlg,
    handleCloseTagManagementDlg,
    handleClearSelection,
    handleGridSelection,
    handleSearch,
    handleClearSearch,
    loading,
    setLoading,
    filteredRows,
    searchText,
    data,
    paginationText,
    paginationModel,
    setPaginationModel,
    openNewWorkOrderDlg,
    selectedRows,
    rowSelectionModel,
    setRowSelectionModel,
    openTagManagementDlg,
    tagManagementData,
    fetchRequests,
    filterButtons,
    handleFilterChange,
    selectedButton,
    jobList
  };

  return {
    IndexProps,
  };
};
